import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import useAxiosInstance from "../api/axiosConfig";
import { useTenant } from "./tenantContext";

interface DesignSystemConfig {
  [key: string]: any;
}

interface DesignSystem {
  defaultLanguage: string;
  cssBasics: DesignSystemConfig;
  logoImageFile: unknown;
  logoImageFileExtended: string;
}

interface DesignSystemContextType {
  designSystem: DesignSystem | null;
  loading: boolean;
  error: Error | null;
}

export const DesignSystemContext = createContext<DesignSystemContextType>({
  designSystem: null,
  loading: true,
  error: null,
});

interface DesignSystemProviderProps {
  children: ReactNode;
}

export function DesignSystemProvider({ children }: DesignSystemProviderProps) {
  const [designSystem, setDesignSystem] = useState<DesignSystem | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const { selectedTenant } = useTenant();
  const axiosInstance = useAxiosInstance();

  useEffect(() => {
    if (!selectedTenant) return;

    setLoading(true);
    setError(null);

    axiosInstance
      .get<DesignSystem>("/rest/tenant/config")
      .then(response => {
        setDesignSystem({
          ...response.data,
          cssBasics: {
            ...response.data.cssBasics,
            primaryColor: response.data.cssBasics.primaryColor || "#0C7878",
            secondaryColor: response.data.cssBasics.secondaryColor || "#00373e",
          },
        });
        setLoading(false);
      })
      .catch(err => {
        console.error("Erro ao carregar design system:", err);
        setError(err);
        setLoading(false);
      });
  }, [selectedTenant]);

  return (
    <DesignSystemContext.Provider value={{ designSystem, loading, error }}>
      {children}
    </DesignSystemContext.Provider>
  );
}

export const useDesignSystem = () => {
  const context = useContext(DesignSystemContext);
  if (context === undefined) {
    throw new Error(
      "useDesignSystem must be used within a DesignSystemProvider"
    );
  }
  return context;
};
